/**
 * La fenêtre FLOTTANTE de l'assistant — l'échange « avec les agents » ouvert
 * depuis une entité de la reconstruction (un local, une pièce, un escalier),
 * sans quitter l'onglet où l'on travaille.
 *
 * Elle se DÉPLACE (glisser l'en-tête), se RÉDUIT (barre de titre seule) et se
 * FERME. Sa position et son état vivent chez l'affaire, persistés au navigateur
 * (voir Affaire) : un rechargement, un F5, une session neuve la retrouvent où
 * on l'a laissée. Le contenu est le chat natif en mode COMPACT — toute la
 * mécanique de conversation, de mission et de flux réutilisée telle quelle ;
 * la conversation, elle, vit au serveur, donc l'échange survit de lui-même.
 */
import { useEffect, useRef, useState } from "react";

import { Chat } from "./Chat";

const LARGEUR = 388;
const HAUTEUR = 560;
const MARGE = 8;
const H_TETE = 44;

interface Position {
  x: number | null;
  y: number | null;
}

/** Cale une position dans la fenêtre — et pose le défaut (coin bas-droit) quand
 *  rien n'a encore été mémorisé. */
function coince(pos: Position, hauteur: number): { x: number; y: number } {
  const largeurVue = typeof window !== "undefined" ? window.innerWidth : 1200;
  const hauteurVue = typeof window !== "undefined" ? window.innerHeight : 800;
  const dx = pos.x ?? largeurVue - LARGEUR - 24;
  const dy = pos.y ?? hauteurVue - hauteur - 24;
  return {
    x: Math.max(MARGE, Math.min(dx, largeurVue - LARGEUR - MARGE)),
    y: Math.max(MARGE, Math.min(dy, hauteurVue - hauteur - MARGE)),
  };
}

export function ChatFlottant({
  affaireId, amorce, onAmorceConsommee, titre = "Assistant",
  minimise, position, onDeplacer, onBasculerMinimise, onFermer,
}: {
  affaireId: string;
  amorce?: string;
  onAmorceConsommee?: () => void;
  titre?: string;
  minimise: boolean;
  position: Position;
  onDeplacer: (x: number, y: number) => void;
  onBasculerMinimise: () => void;
  onFermer: () => void;
}) {
  const cadreRef = useRef<HTMLDivElement | null>(null);
  const glisse = useRef<{ dx: number; dy: number } | null>(null);
  // Position locale pendant le glissé (fluide, sans re-rendre l'affaire à chaque
  // pixel) ; on ne remonte à l'affaire — pour persistance — qu'au relâchement.
  const [pos, poserPos] = useState(() => coince(position, minimise ? H_TETE : HAUTEUR));

  // Recale la fenêtre dans l'écran quand elle est RESTAURÉE (une position calée
  // pour la barre de titre seule laisserait le corps déborder sous l'écran) et
  // quand la FENÊTRE est redimensionnée (sinon la fenêtre part hors-champ, sans
  // poignée pour la rattraper). Ne touche pas au glissé en cours.
  useEffect(() => {
    const caler = () => poserPos((p) => {
      const hauteur = minimise ? H_TETE : HAUTEUR;
      return {
        x: Math.max(MARGE, Math.min(p.x, window.innerWidth - LARGEUR - MARGE)),
        y: Math.max(MARGE, Math.min(p.y, window.innerHeight - hauteur - MARGE)),
      };
    });
    caler();
    window.addEventListener("resize", caler);
    return () => window.removeEventListener("resize", caler);
  }, [minimise]);

  function auDebut(ev: React.PointerEvent) {
    // On ne glisse que depuis l'en-tête, jamais depuis ses boutons.
    if ((ev.target as HTMLElement).closest("button")) return;
    const r = cadreRef.current?.getBoundingClientRect();
    if (!r) return;
    glisse.current = { dx: ev.clientX - r.left, dy: ev.clientY - r.top };
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
  }

  function auMouvement(ev: React.PointerEvent) {
    if (!glisse.current) return;
    const hauteur = minimise ? H_TETE : HAUTEUR;
    const x = Math.max(MARGE,
      Math.min(ev.clientX - glisse.current.dx, window.innerWidth - LARGEUR - MARGE));
    const y = Math.max(MARGE,
      Math.min(ev.clientY - glisse.current.dy, window.innerHeight - hauteur - MARGE));
    poserPos({ x, y });
  }

  function auRelache() {
    if (!glisse.current) return;
    glisse.current = null;
    onDeplacer(pos.x, pos.y);          // mémorise (persistance chez l'affaire)
  }

  return (
    <div ref={cadreRef}
         className={"chat-flottant" + (minimise ? " chat-flottant--reduit" : "")}
         style={{ left: pos.x, top: pos.y, width: LARGEUR,
                  height: minimise ? undefined : HAUTEUR }}
         role="dialog" aria-label={`Échange avec l'assistant — ${titre}`}>
      <div className="chat-flottant__tete" onPointerDown={auDebut}
           onPointerMove={auMouvement} onPointerUp={auRelache}
           onPointerCancel={auRelache} onLostPointerCapture={auRelache}
           onDoubleClick={onBasculerMinimise}>
        <span className="chat-flottant__titre tronque">{titre}</span>
        <div className="chat-flottant__gestes">
          <button type="button" className="chat-flottant__geste"
                  aria-label={minimise ? "Agrandir la fenêtre" : "Réduire la fenêtre"}
                  onClick={onBasculerMinimise}>
            {minimise ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="4" y="4" width="16" height="16" rx="2" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14" />
              </svg>
            )}
          </button>
          <button type="button" className="chat-flottant__geste"
                  aria-label="Fermer la fenêtre" onClick={onFermer}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      </div>
      {/* Le corps reste TOUJOURS monté (caché en CSS quand réduit) : réduire ne
          doit ni perdre le brouillon, ni couper le flux, ni rouvrir un autre
          fil — c'est tout l'intérêt d'une fenêtre qu'on met de côté. */}
      <div className="chat-flottant__corps">
        <Chat affaireId={affaireId} amorce={amorce} compact
              onAmorceConsommee={onAmorceConsommee} />
      </div>
    </div>
  );
}
